import React from 'react';
import { GetTopicSearchResponseRecord, topicTrafficServerProxy } from './serverProxy/topicTrafficServerProxy';
import { useQuery } from '@tanstack/react-query';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { isCustomTopicOrTopicGroup, TopicRequestBuilder, useBaseTopicRequestBuilder } from './utils';
import { TopicType } from '../Topics/types';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

interface TopicSearchResponseRecord extends GetTopicSearchResponseRecord {
  comparisonValues: {
    totalTraffic: number;
    organicTraffic: number;
    paidTraffic: number;
  };
}

const MAX_BARS = 100;

const titlesByMetric = {
  totalTraffic: 'Total Traffic',
  organicTraffic: 'Organic Traffic',
  paidTraffic: 'Paid Traffic'
};

/**
 * Displays the total/organic/paid traffic by topic in a bar chart
 */
export function TrafficByTopicBarChart({ metric }: { metric: 'totalTraffic' | 'organicTraffic' | 'paidTraffic' }) {
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const mainEntity = useMainEntity();
  const isCustom = isCustomTopicOrTopicGroup(mainEntity?.sharedWithEntityId);
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const baseTopicSearchRequest = useBaseTopicRequestBuilder();

  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  // Get the topicIds for the main entity if at the topic group level
  const topicIds = mainEntity?.topicIds ?? [];

  const topicsSearchRequest = new TopicRequestBuilder(baseTopicSearchRequest.build())
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setSortField(metric)
    .setResultCount(500)
    .setTopicType(isCustom ? TopicType.Custom : TopicType.Standard)
    .setRetailerId(+retailerId);

  // If the entity is a topic group, set the topicIds here
  if (isCustom) {
    topicsSearchRequest.setTopicIds(topicIds);
  }

  const topicRequest = topicsSearchRequest.build();

  const topicsSearchComparisonRequest = new TopicRequestBuilder(topicRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek);

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'GetTopicSearchMetrics',
      topicsSearchRequest,
      topicsSearchComparisonRequest.build()
    ],
    queryFn: async () => {
      const response = await topicTrafficServerProxy.getTopicSearchMetrics(topicRequest);

      // If the entity is not a topic group, get all of the topicIds
      if (!isCustom) {
        const ids = response.records.map((record) => record.topicId);
        topicsSearchComparisonRequest.setTopicIds(ids);
      }

      const comparisonResponse = await topicTrafficServerProxy.getTopicSearchMetrics(
        topicsSearchComparisonRequest.build()
      );

      const comparisonTopicIdMap = comparisonResponse.records.reduce((acc, record) => {
        acc.set(record.topicId, record);
        return acc;
      }, new Map<string, GetTopicSearchResponseRecord>());

      const records: TopicSearchResponseRecord[] = response.records.map((record) => ({
        ...record,
        comparisonValues: comparisonTopicIdMap.get(record.topicId)
      }));

      return records;
    }
  });

  const getMainData = (endIndex?: number): [string, number][] => {
    return data?.slice(0, endIndex).map((record) => [record.topicName, record[metric]]) ?? [];
  };

  const getComparisonData = (endIndex?: number): [string, number][] => {
    return data?.slice(0, endIndex).map((record) => [record.topicName, record.comparisonValues?.[metric] ?? 0]) ?? [];
  };

  const categories = data?.map((record) => ({ id: record.topicId, name: record.topicName, type: 'topic' })) ?? [];

  return (
    <BeaconChartWithLegend
      title={`${titlesByMetric[metric]} by Topic`}
      loading={isLoading}
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () =>
        saveBarChartAsDelimitedData(getMainData(), getComparisonData(), {
          fileTitle: `${titlesByMetric[metric]} by Topic`,
          metricLabel: titlesByMetric[metric]
        })
      }
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${titlesByMetric[metric].toLowerCase()} for `}
            {entityPhrase} {taxonomyText}
            {` in each topic`} during the {mainTimePeriodShortDisplayName} period compared to the{' '}
            {comparisonTimePeriodShortDisplayName}.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: getComparisonData(MAX_BARS),
            categories,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: getMainData(MAX_BARS),
            categories,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
}
